import { useEffect, useRef, useSyncExternalStore } from 'react'
import { Check, CircleAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'

/* Toasts (bottom-right, polite live region): what an action did, with an optional "Desfazer".
   Call toast({ message, tone, action }) from anywhere; <Toaster /> is mounted once in AppShell.
   Auto-dismiss after 6 s (10 s with an action); hovering or focusing pauses it. */

export type ToastTone = 'ok' | 'err' | 'info'
export type ToastInput = { message: string; tone?: ToastTone; action?: { label: string; onClick: () => void } }
type ToastItem = ToastInput & { id: number }

let items: ToastItem[] = []
let nextId = 1
const listeners = new Set<() => void>()
const emit = () => listeners.forEach(l => l())

export function toast(input: ToastInput): number {
    const id = nextId++
    items = [...items, { ...input, tone: input.tone ?? 'ok', id }].slice(-4)
    emit()
    return id
}

export function dismissToast(id: number) {
    items = items.filter(t => t.id !== id)
    emit()
}

const subscribe = (l: () => void) => {
    listeners.add(l)
    return () => listeners.delete(l)
}

function ToastView({ t }: { t: ToastItem }) {
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const start = () => {
        timer.current = setTimeout(() => dismissToast(t.id), t.action ? 10_000 : 6_000)
    }
    const stop = () => {
        if (timer.current) clearTimeout(timer.current)
    }
    useEffect(() => {
        start()
        return stop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const Icon = t.tone === 'err' ? CircleAlert : Check
    const color = t.tone === 'err' ? 'var(--red-ink)' : t.tone === 'info' ? 'var(--info)' : 'var(--ok-ink)'
    return (
        <div
            onMouseEnter={stop}
            onMouseLeave={start}
            onFocus={stop}
            onBlur={start}
            className="pointer-events-auto flex w-[min(380px,calc(100vw-32px))] items-start gap-2.5 rounded-[12px] border border-line bg-panel px-3.5 py-3"
            style={{ boxShadow: '0 12px 32px var(--shadow), 0 2px 6px var(--shadow)' }}
        >
            <Icon size={18} className="mt-px flex-none" style={{ color }} aria-hidden="true" />
            <span className="min-w-0 flex-1 text-[13.5px] leading-[1.4] text-fg">{t.message}</span>
            {t.action && (
                <button
                    type="button"
                    className="flex-none cursor-pointer border-0 bg-transparent px-1 text-[13px] font-medium text-green"
                    onClick={() => {
                        t.action?.onClick()
                        dismissToast(t.id)
                    }}
                >
                    {t.action.label}
                </button>
            )}
            <button type="button" aria-label="Fechar aviso" className="grid size-6 flex-none cursor-pointer place-items-center rounded-md border-0 bg-transparent text-muted hover:bg-[var(--hover)]" onClick={() => dismissToast(t.id)}>
                <X size={14} aria-hidden="true" />
            </button>
        </div>
    )
}

export function Toaster() {
    const list = useSyncExternalStore(subscribe, () => items, () => items)
    return (
        <div role="status" aria-live="polite" className={cn('pointer-events-none fixed right-4 bottom-4 z-[60] flex flex-col items-end gap-2')}>
            {list.map(t => <ToastView key={t.id} t={t} />)}
        </div>
    )
}
